<template>
  <!-- Title -->
  <div>
    <!-- Header -->
    <div styles="rnd-xl" class="mleft-02">
      <text-item txt="Ecosystème" styles="font-xl"/>
    </div>
  </div>

  <!-- Body -->
  <div class="overflow-y mtop-30">
    <div class="dashb-body">
      <!-- Timelines on Grid -->
      <div class="tl-grid columns">
        <!-- TL Columns -->
        <template v-for="item in tlColumnArr" :key="item.ID">
          <!-- Date-Labels -->
          <div style="grid-column: timeline; grid-row: 1;">
            <tlLabels :content="item"/>
          </div>
          <!-- Background -->
          <div style="grid-column: timeline" :style="{'grid-row': '2 / ' + lastUiIndex}" class="events-0">
            <tlColumns :content="item"/>
          </div>
        </template>

        <!-- Bilan Types -->
        <template v-for="(item1, index1) in exprList" :key="index1">
          <!-- Roots -->
          <template v-for="(item2, index2) in item1.elements" :key="index2">
            <!-- Proxy Terms -->
            <div :style="{'grid-area' : item2.id.uiIndex + ' / pathoName'}">
              <toolTip>
                <text-item styles="align-r" :txt="item2.proxyTerm"/>
                <template #tooltip>
                  <text-item :txt="item2.proxyTerm" styles="font-s"/>
                </template>
              </toolTip>
            </div>
            <!-- Proxy TL -->
            <div :style="{'grid-area' : item2.id.uiIndex + ' / timeline'}">
              <timeLine :content="item2.tLineContent" styles="bg-bghigh"/>
            </div>
            <!-- LastResult -->
            <div :style="{'grid-area' : item2.id.uiIndex + ' / timeline'}">
              <div class="col w-full">
                <div class="z-base">
                  <template v-if="item2.matchingExpressions?.length > 0">
                    <dropdownBox v-model="dropd" :openOn="'MatchingExpressions' + item2.id.uiIndex">
                      <buttonGroup styles="pad-s border-vl bg-h rnd-0" style="padding: 0.05rem 0.8rem 0.015rem 0.9rem;">
                        <div class="row gap-06">
                          <text-item :txt="item2.matchingExpressions[0].originalText" class="main-h max-w-text"/>
                        </div>
                      </buttonGroup>
                      <template #dropd>
                        <div class="bg-modal w-maxC">
                          <dbGroup>
                            <template v-for="(matchingExpr, index) in item2.matchingExpressions" :key="index">
                              <div class="row gap-06">
                                <text-item :txt="matchingExpr.originalText" class="max-w-text"/>
                                <text-item :txt="matchingExpr.relativeDateText" class="main-m font-s"/>
                              </div>
                            </template>
                          </dbGroup>
                        </div>
                      </template>
                    </dropdownBox>
                  </template>
                </div>
              </div>
            </div>

          </template>

        </template>

        <!-- CS line -->
        <div style="grid-column: timeline" :style="{'grid-row': '2 / ' + lastUiIndex}" class="events-0">
          <tlCsLine :content="tLineCs(csDate, tlStart, tlEnd)"/>
        </div>

      </div>
    </div>
  </div>
</template>




<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCurrentEntryStore } from '@/stores/currentEntry'
import gen from "@/functions/general.js"
import { tLineCs, tLineHeader, tLineExpressions } from "@/functions/timeline.js"
import { getProxyTerm, sortArrayBySiteFindingRoot, sortExprByDate } from '@/functions/expression'
import { apiDate } from "@/api/apicommon"
const currentEntryStore = useCurrentEntryStore()

const dropd = ref("")

const csDate = computed(() => {
  return apiDate(currentEntryStore.currentEntryDate) || new Date()
})

const tlStart = computed(() => {
  const birthDate = apiDate(currentEntryStore.currentPatient?.birthDate)
  const currDate = new Date()
  const startDate = new Date(currDate.setFullYear(currDate.getFullYear() - 6))
  return birthDate || startDate
  //TODO: option to set start date on ui
})


const tlEnd = computed(() => {
  let currDate = apiDate(currentEntryStore.currentEntryDate) || new Date()
  return new Date(currDate.setMonth(currDate.getMonth() + 8))
})

const tlColumnArr = computed(() => {
  return tLineHeader(tlStart.value, tlEnd.value)
})

let uiIndex = 1
const lastUiIndex = ref(0);
function setLastUiIndex() {
  lastUiIndex.value = uiIndex + 1
}

const exprList = ref(null)

function createLists() {
  if (!currentEntryStore.hasCurrentEntry) {return []}

  const allEcosystemConceptGroupIds = currentEntryStore.patientEcosystemResponse
    .map(c => c.conceptGroupId)


  const allExpressions = currentEntryStore.patientEcosystemResponse;
  if (!allExpressions.length) return [];
      
  const exprARR = allExpressions
    .filter(expr => expr.expressionType === "None" && expr.timelineStart !== null && expr.timelineEnd !== null)
    .filter(expr => allEcosystemConceptGroupIds.includes(expr.conceptGroupId))
    .map(expr => gen.deepCopy(expr))
  
  //Group by site-roots
  const siteRoots = exprARR
    .filter(expr => {
      const { siteRoot, siteRootGroup, aggressorRootGroup, aggressorRoot } = expr
      if (siteRoot === "None") return false;
      if (siteRootGroup === "Hemato" && aggressorRootGroup === "Substance") return false;     //Don't show 'calcémie' in 'hémato'
      if (siteRootGroup === "Hemato" && aggressorRoot === 'SubstanceReaction') return false;  //Don't show 'allergie à la pénicilline' in 'hémato'
      return true
    })
    .reduce((result, expr) => {
      const { siteRoot, siteRootGroup, siteRootTerm, siteRootIndex } = expr
      const root = (siteRootGroup === "Pain") ? "Pain" : siteRoot
      const rootTerm = (siteRootGroup === "Pain") ? "Pain" : gen.capitalize(siteRootTerm)
      result[root] = result[root] || {
        "root": root,                     //For grouping
        "rootTerm": rootTerm,             //Term
        "rootIndex": siteRootIndex,       //For sorting
        "rootGroup": siteRootGroup,       //For icon
        "elements": [],
      }
      result[root].elements.push(expr)
      return result
    }, {})

  // Sort site-rootgroups
  let sortedSiteRoots = Object.values(siteRoots)
  sortedSiteRoots.sort((a, b) => a.rootIndex - b.rootIndex)
  sortedSiteRoots.forEach(root => {
    let expressions = gen.deepCopy(root.elements)
    root.elements = groupByProxyKey(expressions, "Site")
  })

  //Group by aggressor-roots
  const aggressorRoots = exprARR
    .filter(expr => expr.aggressorRoot !== "None")
    .reduce((result, expr) => {
      const root = expr.aggressorRoot;
      const rootGroup = expr.aggressorRootGroup
      result[root] = result[root] || {
        "root": root,
        "rootTerm": expr.aggressorRootTerm,
        "rootIndex": expr.aggressorRootIndex,
        "rootGroup": rootGroup,
        "elements": [],
      }
      result[root].elements.push(expr)
      return result
    }, {})

  // Sort aggressor-roots
  let sortedAggressorRoots = Object.values(aggressorRoots)
  sortedAggressorRoots.sort((a, b) => a.rootIndex - b.rootIndex)
  sortedAggressorRoots.forEach(root => {
    let expressions = gen.deepCopy(root.elements)
    root.elements = groupByProxyKey(expressions, "Aggressor")
  })

  //Double None roots
  const noneRoots = exprARR
    .filter(expr => expr.siteRoot === "None" && expr.aggressorRoot === "None")
    .reduce((result, expr) => {
      const root = "None"
      result[root] = result[root] || {
        "root": root,
        "rootTerm": "Other",
        "rootGroup": root,
        "elements": [],
      }
      result[root].elements.push(expr)
      return result
    }, {})

  //Sort none-roots
  let sortedNoneRoots = Object.values(noneRoots)
  sortedNoneRoots.forEach(root => {
    let expressions = gen.deepCopy(root.elements)
    root.elements = groupByProxyKey(expressions, "None")
  })

  //Merge
  let mergedArray = [...sortedSiteRoots, ...sortedAggressorRoots, ...sortedNoneRoots];
  mergedArray = gen.deepCopy(mergedArray)


  //1) List of roots with proxies
  uiIndex = 1
  let retARR = []
  mergedArray.forEach(root => {
    let mainItem = {
      "type": root.rootTerm,   //TODO translate
      "rootGroup": root.rootGroup,
      "id": gen.newId("overviewTitle", ++uiIndex),
    }
    retARR.push(newProxyTl(mainItem, root.elements))
  })
  setLastUiIndex()
  exprList.value = retARR

}


function groupByProxyKey(expressions, category) {
  // category = "Site", "Aggressor", "None"

  //Group
  let groupedExpressions = expressions.reduce((result, expr) => {
    const siteFindingRoot = expr.siteFindingRoot;
    const isAggressor = Boolean(expr.sctAggressor) || Boolean(expr.sctSubstance1)

    if (category === "Site" && siteFindingRoot === "None") return result;
    if (category === "Aggressor" && !isAggressor) return result;

    let proxyKey
    if (category === "None") proxyKey = expr.expressionId
    else proxyKey = expr.sctProxyKey

    let proxyType
    if (category === "Site") proxyType = siteFindingRoot
    else if (category === "Aggressor") proxyType = isAggressor
    else proxyType = "None"

    let proxyTerm
    if (category === "Site") proxyTerm = getProxyTerm(expr, "noSite", "auto")
    else if (category === "Aggressor") proxyTerm = getProxyTerm(expr, "noAggressor", "auto")
    else proxyTerm = expr.originalText

    result[proxyKey] = result[proxyKey] || {
      "proxyKey": proxyKey,     //for proxy-grouping
      "proxyType": proxyType,   //for proxy-order
      "proxyTerm": proxyTerm,
      "elements": [],
    }
    result[proxyKey].elements.push(expr);
    return result;
  }, {});

  //Sort
  let groupedExprArr = Object.values(groupedExpressions)
  if (category === "Site") {sortArrayBySiteFindingRoot(groupedExprArr, "proxyType")}
  //else if (category === "Aggressor") {sortArrayByAggrType(groupedExprArr, "proxyType")}

  return groupedExprArr
}

function newProxyTl(mainItem, proxies) {
  // Main
  // Sort by 1) active medication 2) not longrun medication 3) not in-case medication
  // Sort has to be done before adding uiIndex (those need to be in the correct order)
  // ARR.sort((a, b) => b.active < a.active || a.PathoNom.MédicSiBesoin > b.PathoNom.MédicSiBesoin? 1: -1)
  // Elements

  const proxyARR = gen.deepCopy(proxies)
  const currEntryDate = currentEntryStore.currentEntryDate
  proxyARR.forEach(proxy => {
    const exprARR  = gen.deepCopy(proxy.elements)
    if (exprARR.length > 0) {
      proxy.matchingExpressions = gen.deepCopy(exprARR)
      sortExprByDate(proxy.matchingExpressions)
      proxy.matchingExpressions.forEach(expr => {
        expr.relativeDateText = gen.shortRelativeDate(expr.entry.entryStart, currEntryDate)
      })
    }
    proxy.id = gen.newId("overviewProxy", ++uiIndex),
    proxy.tLineContent = tLineExpressions(exprARR, tlStart.value, tlEnd.value)
  })

  mainItem.elements = proxyARR
  return mainItem
}


onMounted(() => {
  createLists()
})

</script>

<style scoped>
.columns {
  grid-template-columns:
    [pathoName] 9rem
    [empty] 1rem
    [timeline] 1fr;
}
.overflow-y {
  height: calc(100vh - 170px);
  overflow-y: auto;
}
.max-w-text{
  max-width: 10vw;
}
</style>